'use client';
import React, { useState } from 'react';
import Image from 'next/image';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, AlertTriangle, Download } from 'lucide-react';

const recettesData = [
  { mois: 'Jan', recettes: 12400000, depenses: 8200000 },
  { mois: 'Fév', recettes: 14800000, depenses: 9100000 },
  { mois: 'Mar', recettes: 11200000, depenses: 7800000 },
  { mois: 'Avr', recettes: 16500000, depenses: 10200000 },
  { mois: 'Mai', recettes: 18200000, depenses: 11500000 },
  { mois: 'Jun', recettes: 15700000, depenses: 9800000 },
  { mois: 'Jul', recettes: 19400000, depenses: 12100000 },
  { mois: 'Aoû', recettes: 17800000, depenses: 11200000 },
  { mois: 'Sep', recettes: 21200000, depenses: 13400000 },
  { mois: 'Oct', recettes: 20100000, depenses: 12800000 },
  { mois: 'Nov', recettes: 22500000, depenses: 14200000 },
  { mois: 'Déc', recettes: 24800000, depenses: 15600000 },
];

const repartitionRecettes = [
  { name: 'Frais octroi ARPC', value: 38, color: '#1B4F72' },
  { name: 'Cessions PE/PER', value: 22, color: '#D4AC0D' },
  { name: 'Renouvellements', value: 18, color: '#1E8449' },
  { name: 'Dépôts EIES-PGES', value: 12, color: '#E67E22' },
  { name: 'Autres actes', value: 10, color: '#8E44AD' },
];

const repartitionDepenses = [
  { name: 'Salaires et charges', value: 42, color: '#C0392B' },
  { name: 'Fonctionnement', value: 28, color: '#E67E22' },
  { name: 'Investissements', value: 18, color: '#1B4F72' },
  { name: 'Missions terrain', value: 8, color: '#1E8449' },
  { name: 'Divers', value: 4, color: '#8E44AD' },
];

const soldeData = recettesData.map(d => ({
  mois: d.mois,
  solde: d.recettes - d.depenses,
}));

const fmt = (v: number) => `$${(v / 1000000).toFixed(1)}M`;

export default function RapportFinancierPage() {
  const [periode, setPeriode] = useState('2026');

  const totalRecettes = recettesData.reduce((s, d) => s + d.recettes, 0);
  const totalDepenses = recettesData.reduce((s, d) => s + d.depenses, 0);
  const soldeNet = totalRecettes - totalDepenses;
  const tauxCouverture = ((totalRecettes / totalDepenses) * 100).toFixed(1);

  return (
    <div className="p-4 lg:p-6 space-y-5 relative">
      {/* CAMI logo background */}
      <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center opacity-[0.04]">
        <Image
          src="/assets/images/image-1781505606681.png"
          alt=""
          width={500}
          height={500}
          className="object-contain"
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#1B4F72' }}>Rapport Financier Consolidé</h2>
            <p className="text-sm text-gray-500">Recettes et Dépenses — Exercice {periode}</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={periode}
              onChange={e => setPeriode(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
              style={{ borderColor: '#e5e7eb' }}
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
            </select>
            <button
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: '#1B4F72' }}
            >
              <Download size={14} />
              Exporter PDF
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total Recettes', value: fmt(totalRecettes), sub: 'Exercice 2026', color: '#1E8449', bg: '#EAFAF1', icon: TrendingUp },
            { label: 'Total Dépenses', value: fmt(totalDepenses), sub: 'Exercice 2026', color: '#C0392B', bg: '#FDEDEC', icon: TrendingDown },
            { label: 'Solde Net', value: fmt(soldeNet), sub: 'Excédent budgétaire', color: '#1B4F72', bg: '#EBF5FB', icon: DollarSign },
            { label: 'Taux de couverture', value: `${tauxCouverture}%`, sub: 'Recettes / Dépenses', color: '#D4AC0D', bg: '#FEF9E7', icon: AlertTriangle },
          ].map((k, i) => {
            const KIcon = k.icon;
            return (
              <div key={i} className="bg-white rounded-xl border p-4 flex items-start gap-3" style={{ borderColor: '#e5e7eb' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: k.bg }}>
                  <KIcon size={18} style={{ color: k.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold" style={{ color: k.color }}>{k.value}</p>
                  <p className="text-xs font-medium text-gray-700">{k.label}</p>
                  <p className="text-xs text-gray-400">{k.sub}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recettes vs Dépenses chart */}
        <div className="bg-white rounded-xl border p-4 mb-5" style={{ borderColor: '#e5e7eb' }}>
          <h3 className="font-semibold text-gray-800 mb-4">Évolution Recettes vs Dépenses — 2026</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={recettesData}>
              <defs>
                <linearGradient id="gradRec" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1E8449" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1E8449" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradDep" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C0392B" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#C0392B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={fmt} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Legend />
              <Area type="monotone" dataKey="recettes" name="Recettes" stroke="#1E8449" fill="url(#gradRec)" strokeWidth={2} />
              <Area type="monotone" dataKey="depenses" name="Dépenses" stroke="#C0392B" fill="url(#gradDep)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Solde net + Répartitions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          {/* Solde mensuel */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800 mb-3">Solde mensuel net</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={soldeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mois" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v: number) => fmt(v)} />
                <Bar dataKey="solde" name="Solde" fill="#1B4F72" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Répartition recettes */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800 mb-3">Répartition des recettes</h3>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={repartitionRecettes} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value">
                  {repartitionRecettes.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {repartitionRecettes.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color }} />
                  <span className="text-gray-600 flex-1">{r.name}</span>
                  <span className="font-semibold" style={{ color: r.color }}>{r.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Répartition dépenses */}
          <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800 mb-3">Répartition des dépenses</h3>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={repartitionDepenses} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value">
                  {repartitionDepenses.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => `${v}%`} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1 mt-2">
              {repartitionDepenses.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.color }} />
                  <span className="text-gray-600 flex-1">{r.name}</span>
                  <span className="font-semibold" style={{ color: r.color }}>{r.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tableau récapitulatif */}
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#e5e7eb' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: '#e5e7eb' }}>
            <h3 className="font-semibold text-gray-800">Récapitulatif mensuel — 2026</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#F4F6F8' }}>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600">Mois</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-600">Recettes (USD)</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-600">Dépenses (USD)</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-600">Solde (USD)</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-600">Taux couverture</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#f0f0f0' }}>
                {recettesData.map((row, i) => {
                  const solde = row.recettes - row.depenses;
                  const taux = ((row.recettes / row.depenses) * 100).toFixed(0);
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{row.mois} 2026</td>
                      <td className="px-4 py-2.5 text-right font-semibold" style={{ color: '#1E8449' }}>
                        {row.recettes.toLocaleString('fr-FR')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold" style={{ color: '#C0392B' }}>
                        {row.depenses.toLocaleString('fr-FR')}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold" style={{ color: solde >= 0 ? '#1E8449' : '#C0392B' }}>
                        {solde >= 0 ? '+' : ''}{solde.toLocaleString('fr-FR')}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span
                          className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            background: Number(taux) >= 150 ? '#EAFAF1' : Number(taux) >= 120 ? '#FEF9E7' : '#FDEDEC',
                            color: Number(taux) >= 150 ? '#1E8449' : Number(taux) >= 120 ? '#D4AC0D' : '#C0392B',
                          }}
                        >
                          {taux}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
